import { Component, inject, signal } from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { ActivatedRoute, RouterOutlet } from '@angular/router'
import { defaultIfEmpty, distinctUntilChanged, EMPTY, Observable, switchMap, tap } from 'rxjs'

import { HeaderImageService } from '@services/header-image'
import { VehicleInfoContextState } from '@states/vehicle-info-context-state'

@Component({
	selector: 'vehicle-info-layout',
	templateUrl: './vehicle-info-layout.component.html',
	styleUrl: './vehicle-info-layout.component.scss',
	imports: [RouterOutlet],
})
export class VehicleInfoLayoutComponent {
	readonly #route = inject(ActivatedRoute)
	readonly #contextState = inject(VehicleInfoContextState)
	readonly #imageService = inject(HeaderImageService)

	readonly partnerLogoUrl = signal<string | null>(null)
	// logoResolved distinguishes "still waiting for S3" (skeleton) from
	// "S3 responded and there's no partner logo" (Corepark solo). Both
	// have partnerLogoUrl === null; only the resolved case should collapse
	// the header to a single-column solo layout.
	readonly logoResolved = signal<boolean>(false)

	constructor() {
		toObservable(this.#contextState.operatorUuid)
			.pipe(
				distinctUntilChanged(),
				tap(() => {
					this.partnerLogoUrl.set(null)
					this.logoResolved.set(false)
				}),
				switchMap((operatorUuid): Observable<string | null> => {
					if (!operatorUuid) return EMPTY
					const locationUuid = this.#childLocationUuid()
					return this.#imageService
						.getImage(operatorUuid, locationUuid)
						.pipe(defaultIfEmpty(null))
				}),
				takeUntilDestroyed()
			)
			.subscribe((url) => {
				this.partnerLogoUrl.set(url)
				this.logoResolved.set(true)
			})
	}

	#childLocationUuid(): string | undefined {
		return this.#route.firstChild?.snapshot.paramMap.get('locationUUID') ?? undefined
	}
}
